import { useCurrentUser } from "@src/context/UserContext";
import { VITE_AUTH_SERVICE_BASE_URL } from "@src/env";
import {
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
  recursiveSnakeCaseCipherTypesafe,
} from "@src/global_functions/postgrestApi";
import request from "@src/global_functions/request";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as R from "ramda";
import {
  CamelCasedProperties,
  CamelCasedPropertiesDeep,
  RequiredKeysOf,
} from "type-fest";
import FetchClients, { ClientOutput } from "../client";
import {
  BackpackServiceError,
  isBackpackResponseErrorBody,
  throwBackpackSuperagentResponseError,
} from "../errors";
import { ACCOUNT_SUMMARY_KEY } from "../postgrest/accounts";

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export type CreateSuperAdminBody = CamelCasedPropertiesDeep<
  ClientOutput["auth"]["CreateBackpackAdminReq"]
>;
export type User = CamelCasedPropertiesDeep<ClientOutput["auth"]["User"]>;
type EditUserBody = CamelCasedPropertiesDeep<
  ClientOutput["auth"]["EditUserReq"]
>;

export const SIGNED_IN_USER_DELETE_ATTEMPT_ERROR_MESSAGE =
  "Cannot delete currently signed in user.";

const users = {
  mutations: {
    useCreateAndInviteUser,
    useAddSuperAdmin,
    useDelete,
    useBulkDelete,
    useRequestPasswordReset,
    useEditUser,
    usePostSitesToUserAllowList,
    useBulkUploadCsv,
  },
} as const;

export default users;

export type CreateAndInviteUserBody = CamelCasedPropertiesDeep<
  ClientOutput["auth"]["CreateUserReq"]
>;
type CreateAndInviteUserInput = Partial<Nullable<CreateAndInviteUserBody>>;

type RequiredFields = RequiredKeysOf<CreateAndInviteUserBody>;
const hasRequiredFields = (
  input: CreateAndInviteUserInput
): input is CreateAndInviteUserBody => {
  const requiredFields: RequiredFields[] = [
    "accountId",
    "firstName",
    "lastName",
    "email",
    "phoneNumber",
    "userRole",
    "userPermissions",
    "allowedSiteIds",
  ];
  return requiredFields.every((field) => {
    const val = input[field];
    return typeof val !== "undefined" && val !== null;
  });
};

const createAndInviteUser = async (input: CreateAndInviteUserInput) => {
  if (!hasRequiredFields(input))
    throw new Error("Missing required fields to create user.");
  const { data, error } = await FetchClients.auth.POST("/user/create", {
    body: recursiveSnakeCaseCipher(input),
  });
  if (data) return recursiveCamelCaseCipher(data) as User;

  if (isBackpackResponseErrorBody(error)) {
    throw new BackpackServiceError(error);
  }

  throw new Error(
    `Failed to create user: ${
      (error as unknown as Error)?.message || "Unknown Error"
    }`
  );
};

function useCreateAndInviteUser() {
  return useMutation({
    mutationFn: createAndInviteUser,
  });
}

async function editUserInfo({
  userId,
  updatedProps,
}: {
  userId: number;
  updatedProps: EditUserBody;
}) {
  const { response, error } = await FetchClients.auth.PATCH("/user/edit_user", {
    params: {
      query: { user_id: userId },
    },
    body: recursiveSnakeCaseCipherTypesafe(updatedProps),
  });
  if (response.ok) return;
  throw new Error(error);
}

type AllowListBody = CamelCasedProperties<
  ClientOutput["site"]["UserSiteAccessUpdate"]
>;

// post the siteIds a user can access
const postSitesToUserAllowList = async (body: AllowListBody) => {
  const { response, error } = await FetchClients.site.POST("/allowlist", {
    body: recursiveSnakeCaseCipher(body),
  });
  if (response.ok) return;
  throw new Error(error);
};

function usePostSitesToUserAllowList() {
  return useMutation({
    mutationFn: postSitesToUserAllowList,
  });
}

function useEditUser() {
  return useMutation({
    mutationFn: editUserInfo,
  });
}

const addSuperAdmin = async (body: CreateSuperAdminBody) => {
  const { data, error } = await FetchClients.auth.POST(
    "/user/create_backpack_admin",
    {
      body: recursiveSnakeCaseCipher(body),
    }
  );
  if (data) return recursiveCamelCaseCipher(data) as User;

  throw new Error(
    `Failed to create user: ${
      (error as unknown as Error)?.message || "Unknown Error"
    }`
  );
};
function useAddSuperAdmin() {
  return useMutation({
    mutationFn: addSuperAdmin,
  });
}

const deleteUser = async (user_id: number) => {
  const { response, error } = await FetchClients.auth.PATCH(
    "/user/delete_user",
    {
      params: {
        query: { user_id },
      },
    }
  );
  if (response.ok) return;
  throw new Error(
    `Failed to delete user ID ${user_id}: ${
      (error as unknown as Error)?.message || "Unknown Error"
    }`
  );
};

function useDelete() {
  const currentUser = useCurrentUser();
  return useMutation({
    mutationFn: (userId: number) => {
      if (currentUser.id === userId) {
        throw new Error(SIGNED_IN_USER_DELETE_ATTEMPT_ERROR_MESSAGE);
      }
      return deleteUser(userId);
    },
  });
}

function useBulkDelete() {
  const currentUser = useCurrentUser();
  return useMutation({
    mutationFn: async (userIds: number[]) => {
      if (currentUser.id && userIds.includes(currentUser.id)) {
        throw new Error(SIGNED_IN_USER_DELETE_ATTEMPT_ERROR_MESSAGE);
      }
      const results = await Promise.allSettled(
        userIds.map((userId) => deleteUser(userId))
      );

      const errors = results
        .filter(
          (result): result is PromiseRejectedResult =>
            result.status === "rejected"
        )
        .map(R.prop("reason"))
        .filter((error): error is Error => error instanceof Error);

      if (errors.length) {
        throw new Error(`${R.map(R.prop("message"), errors).join()}`);
      }
    },
  });
}

function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { response, error } = await FetchClients.auth.POST(
        "/user/reset_password",
        {
          body: {
            email,
          },
        }
      );
      if (response.ok) return;
      throw new Error(error);
    },
  });
}

function useBulkUploadCsv() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (csv: File) => {
      try {
        await request
          .post(`${VITE_AUTH_SERVICE_BASE_URL}/auth/user/bulk_upload`)
          .send(csv);
      } catch (error) {
        throwBackpackSuperagentResponseError(error);
      }
    },
    onSuccess: async () => {
      return queryClient.invalidateQueries({
        // we can upload users to any number of accounts/sites, so we need to invalidate the queries for all accounts
        queryKey: [ACCOUNT_SUMMARY_KEY],
      });
    },
  });
}
